import React, { useState } from 'react';
import styled from 'styled-components';
import MetaTags from 'react-meta-tags';

import ContentContainerDefault from '~/components/ContentContainer/Default';
import OverviewListHeader from '~/components/OverviewListHeader';
import { AddUserSidebar } from '~/scenes/Settings/components';
import useConfirmModal from '~/components/Alerts/useConfirmModal';
import {
  useGetSessionHydrationQuery,
  UserStatus,
  useUpdateOfficeRelationMutation,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import { NoResultSection, AppErrorScreen } from '~/components';
import OfficeUserCard from '../OfficeUserCard';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import type { RouteComponentProps } from '@reach/router';
import Catalog from '~/Catalog';
import useOffice from '~/hooks/useOffice';
import useUsers from '~/hooks/useUsers';
import { sortByName } from '~/scenes/Settings/Users/utils';
import { isNil } from 'ramda';
import useUserRights from '~/hooks/useUserRights';
import Button from '~/components/Button';

export const text = {
  users: 'Gebruikers',
  offices: 'Vestigingen',
  noResults: Catalog.noResults,
  confirmRoleChangeTitle: 'Weet je zeker dat je de rol wil aanpassen?',
  confirmRoleChangeMsg: 'Het is altijd mogelijk om de rol weer terug te zetten',
  userUpdateError: Catalog.genericUnknownErrorMessage,
  addUserButton: 'Nieuwe gebruiker',
  confirmModalText: 'Ja ik weet het zeker',
  pageTitle: 'Gebruikerslijst',
};

export type Props = RouteComponentProps<{
  dataTestId?: string;
  officeId: string;
  hasError: boolean;
}>;

const OfficeUserList: React.FC<Props> = ({
  dataTestId,
  hasError,
  officeId,
  ...rest
}) => {
  const { id: accountId } = useCurrentAccount();
  const addToast = useAddToast();
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const { refetch } = useGetSessionHydrationQuery({
    variables: { accountId },
  });

  const office = useOffice(officeId);
  const officeUsers = useUsers({
    statuses: [
      UserStatus.Active,
      UserStatus.Pending,
      UserStatus.InvitedPending,
    ],
    officeIds: [officeId],
  });

  const { update: accountAdminMayUpdate } = useUserRights({
    category: 'Offices',
  });
  const { update: officeAdminMayUpdate } = useUserRights({
    category: 'Offices',
    entity: { type: 'office', id: officeId },
  });

  const mayEdit = accountAdminMayUpdate || officeAdminMayUpdate;

  const [showConfirmModal, setOnConfirm] = useConfirmModal(
    {
      title: text.confirmRoleChangeTitle,
      message: text.confirmRoleChangeMsg,
      buttonConfirmTitle: text.confirmModalText,
    },
    () => {},
    () => {},
    undefined,
    false,
  );

  const [updateOfficeRelation, { loading }] = useUpdateOfficeRelationMutation();

  if (isNil(officeId))
    return <NoResultSection>{Catalog.noResults}</NoResultSection>;

  const updateUserRole = ({ userId, role }) => {
    void updateOfficeRelation({
      variables: {
        accountId,
        officeId,
        userId,
        update: {
          role,
        },
      },
    }).then(({ errors }) => {
      if (errors && errors.length > 0) {
        addToast([formatToastMessage(text.userUpdateError, 'danger')]);
        return;
      }

      return refetch();
    });
  };

  const sortedUsers = sortByName(officeUsers);

  if (hasError || !officeId || !office) return <AppErrorScreen />;

  const officeName = office ? office.name : '';

  const ifUsersInOffice = sortedUsers.length !== 0;

  return (
    <Container data-testid={dataTestId} {...rest}>
      <ContentContainerDefault
        breadcrumbs={[
          {
            to: '/-/settings/offices',
            label: text.offices,
          },
          {
            to: `/-/settings/offices/${officeId}`,
            label: officeName,
          },
          {
            label: text.users,
          },
        ]}
      >
        <MetaTags>
          <title>{text.pageTitle}</title>
        </MetaTags>

        <>
          <OverviewListHeader
            title={officeName}
            buttons={[
              mayEdit
                ? {
                    node: (
                      <Button
                        appearance="secondary"
                        onClick={() => setShowSidebar(true)}
                        dataTestId="add-user-button"
                        label={text.addUserButton}
                        size="medium"
                      />
                    ),
                    key: 'add-user-button',
                  }
                : undefined,
            ]}
          />
          {mayEdit && (
            <AddUserSidebar
              officeOptions={[
                {
                  key: office?.id,
                  payload: office,
                  label: office?.name,
                },
              ]}
              accountId={accountId}
              onUserAdded={refetch}
              hide={() => setShowSidebar(false)}
              isShowing={showSidebar}
            />
          )}

          {!ifUsersInOffice && (
            <NoResultSection>{text.noResults}</NoResultSection>
          )}

          {ifUsersInOffice != null && (
            <section data-testid="user-list">
              {sortedUsers.map(({ id }) => (
                <OfficeUserCard
                  key={id}
                  onRoleChange={(userId, role) => {
                    setOnConfirm(() => updateUserRole({ userId, role }));
                    showConfirmModal();
                  }}
                  loading={loading}
                  officeId={officeId}
                  userId={id}
                  canEdit={mayEdit}
                />
              ))}
            </section>
          )}
        </>
      </ContentContainerDefault>
    </Container>
  );
};

const Container = styled.div<{}>``;

export default OfficeUserList;
